import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "关于本站", to: "/about" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const word = keyword.trim();
    navigate(word ? `/search?q=${encodeURIComponent(word)}` : "/search");
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-gradient-to-b from-background-50 via-background-50/70 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-foreground-50 flex items-center justify-center transition-colors group-hover:bg-accent-500">
              <i className="ri-film-line text-lg md:text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-base md:text-xl font-bold tracking-wide text-foreground-50 whitespace-nowrap">
                淫乱小镇
              </span>
              <span className="hidden sm:block font-label text-[10px] tracking-[0.32em] text-foreground-500 mt-1">
                FILM ARCHIVE
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative px-3 lg:px-4 py-2 text-sm rounded-md transition-colors cursor-pointer ${
                    isActive
                      ? "text-foreground-50 font-medium"
                      : "text-foreground-500 hover:text-foreground-50"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-[2px] rounded-full bg-accent-500 transition-all duration-300 ${
                        isActive ? "w-5" : "w-0"
                      }`}
                    ></span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className="w-40 lg:w-56 h-9 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/50 transition-all"
              />
            </form>
            <Link
              to="/category"
              className="hidden lg:inline-flex h-9 px-4 items-center gap-1.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-compass-3-line"></i>
              全部作品
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-100 cursor-pointer"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-4 animate-slide-down">
          <form onSubmit={handleSearch} className="relative mb-4">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="text"
              placeholder="搜索影视作品"
              aria-label="搜索影视作品"
              className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400"
            />
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between px-1 py-3 border-b border-background-200/60 text-sm ${
                    isActive ? "text-accent-500 font-medium" : "text-foreground-300"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-600"></i>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}